import { Box } from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import type { ColumnsType } from '@setvi/shared/types';
import moment from 'moment';
import { Stock } from 'pages/products/components/stock';
import { ModifiableProduct } from 'pages/sales/modules/orders/subpages/orders/subpages/order-create';
import { ProductNameCell } from './product-name-cell';
import { QuantityCell } from './quantity-cell';

const formatCurrency = (value: number) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

interface BuildColumnsArgs {
  searchQuery?: string;
  selectedProducts: ModifiableProduct[];
  onProductChange: (
    product: ModifiableProduct,
    newValues: Partial<ModifiableProduct>
  ) => void;
  onStockRefresh: (
    id: ModifiableProduct['_id'],
    newValues: Partial<ModifiableProduct>
  ) => void;
  stockLabel?: string;
  // At/below 1180px tighten the column floors so the row fits without a
  // horizontal scroll. Above 1180px the original widths are used.
  isCompact?: boolean;
}

export const buildOrderHistoryColumns = ({
  searchQuery,
  selectedProducts,
  onProductChange,
  onStockRefresh,
  stockLabel = 'Stock',
  isCompact = false
}: BuildColumnsArgs): ColumnsType<ModifiableProduct> => [
  {
    id: 'product',
    label: 'Product Name & SKU',
    minWidth: isCompact ? 220 : 320,
    align: 'left',
    cellContent: (product: ModifiableProduct) => (
      <ProductNameCell product={product} searchQuery={searchQuery} />
    )
  },
  {
    id: 'stock',
    label: stockLabel,
    minWidth: isCompact ? 90 : 110,
    align: 'center',
    cellContent: (product: ModifiableProduct) => (
      <Box display="flex" justifyContent="center">
        <Stock
          stockRefresh={{
            id: product._id,
            onStockRefresh: newInventory =>
              onStockRefresh(product._id, { inventory: newInventory })
          }}
          inventory={product.inventory}
          isDropship={product.dropship}
          stockInfo={product.stockInfo}
          isStockProduct={product?.isStockProduct}
        />
      </Box>
    )
  },
  {
    id: 'quantity',
    label: 'Quantity',
    minWidth: isCompact ? 140 : 160,
    align: 'center',
    cellContent: (product: ModifiableProduct) => {
      const selected = selectedProducts.find(p => p.sku === product.sku);
      const displayedQuantity = selected?.quantity ?? product.quantity ?? 0;
      const step =
        product?.minimumQuoteQuantity > 1 &&
        product?.overrideMinimumQuoteQuantity !== true
          ? product.minimumQuoteQuantity
          : 1;

      return (
        <QuantityCell
          quantity={displayedQuantity}
          step={step}
          onChange={value => onProductChange(product, { quantity: value })}
        />
      );
    }
  },
  {
    id: 'lastQty',
    label: 'Last Qty',
    minWidth: isCompact ? 70 : 80,
    align: 'center',
    cellContent: (product: ModifiableProduct) => (
      <Box display="flex" justifyContent="center">
        <SText
          title={(product?.lastSoldQuantity ?? 0).toLocaleString()}
          weight="semibold"
        />
      </Box>
    )
  },
  {
    id: 'lastDate',
    label: 'Last Date',
    minWidth: isCompact ? 90 : 110,
    align: 'center',
    cellContent: (product: ModifiableProduct) => (
      <Box display="flex" justifyContent="center">
        <SText
          title={
            product?.createdDate
              ? moment(product.createdDate).format('MM/DD/YYYY')
              : '-'
          }
        />
      </Box>
    )
  },
  {
    id: 'lastPrice',
    label: 'Last Price',
    minWidth: isCompact ? 110 : 130,
    align: 'center',
    cellContent: (product: ModifiableProduct) => (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gridGap={2}>
        <SText
          title={formatCurrency(product?.sell ?? 0)}
          weight="semibold"
          fontColor={TextColor.Blue}
        />
        <SText
          title={formatCurrency(product?.cost ?? 0)}
          prefix={<span style={{ fontWeight: 'normal' }}>{`Cost: `}</span>}
        />
      </Box>
    )
  },
  {
    id: 'lastGp',
    label: 'Last GP',
    minWidth: isCompact ? 90 : 100,
    align: 'center',
    cellContent: (product: ModifiableProduct) => {
      const sell = product?.sell ?? 0;
      const cost = product?.cost ?? 0;
      const gp = sell - cost;
      const gpPercent = sell > 0 ? (gp / sell) * 100 : 0;
      const color = gp >= 0 ? TextColor.Green : TextColor.Red;
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gridGap={2}>
          <SText
            title={formatCurrency(gp)}
            weight="semibold"
            fontColor={color}
          />
          <SText
            title={`${gpPercent.toFixed(2)}%`}
            weight="semibold"
            fontColor={color}
          />
        </Box>
      );
    }
  }
];
